// Donjon : nom, salle actuelle (type, description), progression (salles visitées / total), membres, boutons avancer / quitter ; bouton « Entrer » si le lieu a un donjon.
import { useState } from "preact/hooks";
import type { ClientView, DungeonView, Id, RoomKind } from "../../shared/types";
import { send } from "../store";
import "./DungeonPanel.css";

export interface DungeonPanelProps { view: ClientView }

const ROOM_ICON: Record<RoomKind, string> = {
  entree: "🚪", couloir: "🕯️", piege: "⚠️", enigme: "🧩", combat: "⚔️", tresor: "💰", repos: "🔥", boss: "👑",
};
const ROOM_LABEL: Record<RoomKind, string> = {
  entree: "Entrée", couloir: "Couloir", piege: "Piège", enigme: "Énigme",
  combat: "Combat", tresor: "Trésor", repos: "Lieu de repos", boss: "Salle du boss",
};

function salles(n: number): string {
  return `${n} salle${n > 1 ? "s" : ""}`;
}

export function DungeonPanel(props: DungeonPanelProps) {
  const { view } = props;
  const inCombat = view.combat !== null;
  if (view.dungeon) return <DungeonRun dungeon={view.dungeon} inCombat={inCombat} />;
  if (view.character && view.location?.dungeonId) return <DungeonCall inCombat={inCombat} />;
  return null;
}

function DungeonRun({ dungeon, inCombat }: { dungeon: DungeonView; inCombat: boolean }) {
  const { room } = dungeon;
  const [askLeave, setAskLeave] = useState(false);
  const cleared = room.cleared;
  const nextIds: Id[] = room.next;
  const advanceBlocked = inCombat || !cleared || nextIds.length === 0;

  // Le nom des salles suivantes n'est pas dans la vue : on distingue les passages par leur numéro.
  const hint = inCombat
    ? "Le combat d’abord : tu ne peux ni avancer ni retenter la salle pendant la bataille."
    : !cleared
      ? "Nettoie la salle pour avancer, ou retente-la."
      : nextIds.length === 0
        ? "Aucun passage ne mène plus loin pour l’instant."
        : "";

  const advance = (roomId: Id) => send({ t: "dungeon_advance", roomId });

  return (
    <section class="dungeon-panel" aria-label={`Donjon : ${dungeon.name}`}>
      <header class="dungeon-panel-head">
        <h2 class="dungeon-panel-title">
          <span aria-hidden="true">🏰</span> {dungeon.name}{" "}
          <span class="dungeon-panel-level">· niveau {dungeon.level}</span>
        </h2>
        <p class="dungeon-panel-theme">{dungeon.theme}</p>
      </header>

      <div class="dungeon-panel-room">
        <span class="dungeon-panel-room-icon" aria-hidden="true">{ROOM_ICON[room.kind]}</span>
        <div class="dungeon-panel-room-head">
          <span class="dungeon-panel-room-kind">{ROOM_LABEL[room.kind]}</span>
          <h3 class="dungeon-panel-room-name">{room.name}</h3>
        </div>
      </div>

      <p class="dungeon-panel-room-text">{room.description}</p>
      {room.monsters.length > 0 && !cleared ? <p class="dungeon-panel-threat">⚔️ Des créatures rôdent ici.</p> : null}
      {room.puzzle && !cleared ? <p class="dungeon-panel-puzzle">🧩 {room.puzzle.text}</p> : null}

      <div class="dungeon-panel-progress-block">
        <p class="dungeon-panel-progress-label">
          {salles(dungeon.visitedCount)} visitée{dungeon.visitedCount > 1 ? "s" : ""} sur {dungeon.totalRooms}
        </p>
        <ol class="dungeon-panel-progress" aria-label={`${dungeon.visitedCount} salles visitées sur ${dungeon.totalRooms}`}>
          {Array.from({ length: dungeon.totalRooms }, (_, i) => (
            <li
              key={i}
              class={[
                "dungeon-panel-pill",
                i < dungeon.visitedCount ? "dungeon-panel-pill--on" : "",
                i === dungeon.visitedCount - 1 ? "dungeon-panel-pill--current" : "",
              ].filter(Boolean).join(" ")}
            />
          ))}
        </ol>
      </div>

      {dungeon.members.length > 0 ? (
        <p class="dungeon-panel-members">Compagnons : {dungeon.members.join(", ")}</p>
      ) : null}

      <div class="dungeon-panel-actions">
        {!cleared ? (
          <button
            type="button"
            class="btn-primary dungeon-panel-retry"
            disabled={inCombat}
            onClick={() => advance(room.id)}
          >
            <span aria-hidden="true">🔁</span> Retenter la salle
          </button>
        ) : null}
        {nextIds.length <= 1 ? (
          <button
            type="button"
            class="btn-primary dungeon-panel-advance"
            disabled={advanceBlocked}
            onClick={() => {
              const next = nextIds[0];
              if (next !== undefined) advance(next);
            }}
          >
            <span aria-hidden="true">➡️</span> Avancer
          </button>
        ) : (
          nextIds.map((id, i) => (
            <button
              key={id}
              type="button"
              class="btn-primary dungeon-panel-advance"
              disabled={advanceBlocked}
              onClick={() => advance(id)}
            >
              <span aria-hidden="true">➡️</span> Prendre le passage {i + 1}
            </button>
          ))
        )}

        {askLeave ? (
          <div class="dungeon-panel-confirm">
            <button
              type="button"
              class="btn-danger"
              disabled={inCombat}
              onClick={() => {
                setAskLeave(false);
                send({ t: "dungeon_leave" });
              }}
            >
              Oui, quitter le donjon
            </button>
            <button type="button" class="btn-ghost" onClick={() => setAskLeave(false)}>Rester</button>
          </div>
        ) : (
          <button type="button" class="btn-ghost dungeon-panel-leave" disabled={inCombat} onClick={() => setAskLeave(true)}>
            Quitter le donjon
          </button>
        )}
      </div>

      {hint !== "" ? <p class="dungeon-panel-hint" aria-live="polite">{hint}</p> : null}
    </section>
  );
}

function DungeonCall({ inCombat }: { inCombat: boolean }) {
  return (
    <section class="dungeon-panel dungeon-panel--call" aria-label="Donjon à proximité">
      <span class="dungeon-panel-call-icon anim-glow" aria-hidden="true">🏰</span>
      <h2 class="dungeon-panel-call-title">Un donjon s’ouvre ici…</h2>
      <p class="dungeon-panel-call-text">
        Une entrée sombre s’enfonce dans la pierre. Les compagnons présents sur les lieux te suivront.
      </p>
      <button type="button" class="btn-primary dungeon-panel-enter" disabled={inCombat} onClick={() => send({ t: "dungeon_enter" })}>
        Entrer
      </button>
      {inCombat ? <p class="dungeon-panel-hint">Le combat d’abord.</p> : null}
    </section>
  );
}
